import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface PdfViewerModalProps {
  /** Blob object URL of the PDF; when null the modal is closed. */
  url: string | null;
  title?: string;
  loading?: boolean;
  onDownload?: () => void;
  onClose: () => void;
}

/**
 * In-app PDF viewer: renders a PDF blob URL inside an iframe so a user can read
 * the document without downloading it. Fetching (with auth) happens in the
 * caller; this just displays the resulting object URL. Requires `blob:` in the
 * CSP frame-src (see public/index.html).
 */
export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({ url, title = 'Invoice', loading = false, onDownload, onClose }) => {
  const { t } = useTranslation();
  const open = !!url || loading;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex h-[90vh] flex-col gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="flex-row items-center justify-between border-b px-4 py-3">
          <DialogTitle>{title}</DialogTitle>
          {onDownload && (
            // Sits beside the close control rather than in a footer: the
            // document fills the dialog, so there is no footer to put it in.
            <Button variant="outline" size="sm" className="me-8" onClick={onDownload}>
              <Download className="size-4" />
              {t('COMMON.DOWNLOAD')}
            </Button>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 bg-muted">
          {url ? (
            <iframe title={title} src={url} className="block size-full border-0" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="size-7 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerModal;
